import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Link, Spinner } from '@apollo/orbit';
import React, { useCallback, useEffect, useState } from 'react';

import { userSettingsRouteConfig } from 'src/app/account/routes';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import {
  GraphType,
  OnboardingArchitecture,
  ProvisionDefaultKey,
  ProvisionDefaultKeyVariables,
  ProvisionDefaultKey_me,
  ProvisionDefaultKey_me_UserMutation,
  PublishSchemaApiKeysQuery,
  PublishSchemaApiKeysQueryVariables,
  PublishSchemaGraphFragment,
} from 'src/lib/graphqlTypes/types';

import { HeaderTab } from '../common/headerTab/HeaderTab';
import { ErrorMessage } from '../errorMessage/ErrorMessage';

import { ContinuousIntegrationTab } from './continuousIntegrationTab/ContinuousIntegrationTab';
import { EndpointTab } from './endpointTab/EndpointTab';
import { FederationTab } from './federationTab/FederationTab';
import { PublishUsingFederation } from './publishUsingFederation/PublishUsingFederation';
import { SchemaFileTab } from './schemaFileTab/SchemaFileTab';
import { SchemaReportingTab } from './schemaReportingTab/SchemaReportingTab';
import { PublishModeTab } from './shared';

const provisionDefaultKey = gql<
  ProvisionDefaultKey,
  ProvisionDefaultKeyVariables
>`
  mutation ProvisionDefaultKey {
    me {
      ... on UserMutation {
        user {
          id
        }
        provisionKey {
          apiKey {
            id
            keyName
            token
          }
          created
        }
      }
    }
  }
`;

export const publishSchemaGraphFragment = gql`
  fragment PublishSchemaGraphFragment on Service {
    account {
      id
    }
    id
    graphType
    onboardingArchitecture
    variant(name: $graphVariant) @skip(if: $noVariant) {
      id
      name
      hasManagedSubgraphs
      latestPublication {
        id
        schema {
          createdAt
          hash
          document
        }
      }
      latestLaunch {
        id
        build {
          id
          result {
            ... on BuildFailure {
              errorMessages {
                message
              }
            }
          }
        }
      }
    }
  }
`;

const publishSchemaApiKeysQuery = gql<
  PublishSchemaApiKeysQuery,
  PublishSchemaApiKeysQueryVariables
>`
  query PublishSchemaApiKeysQuery($graphId: ID!) {
    graph(id: $graphId) {
      id
      apiKeys {
        id
        keyName
        token
      }
    }
  }
`;

export function isPublished(graph: PublishSchemaGraphFragment) {
  return Boolean(graph.variant?.latestPublication?.id);
}

function hasBuildErrors(graph: PublishSchemaGraphFragment) {
  return (
    graph.variant?.latestLaunch?.build?.result?.__typename === 'BuildFailure'
  );
}

export type GraphArchitecture = 'federation' | 'monolith' | 'unknown';

export function determineGraphArchitecture(
  graph?: PublishSchemaGraphFragment | null,
): GraphArchitecture {
  return graph?.onboardingArchitecture === OnboardingArchitecture.SUPERGRAPH ||
    graph?.variant?.hasManagedSubgraphs
    ? 'federation'
    : graph
      ? 'monolith'
      : 'unknown';
}

function isUserMutation(
  mutation: ProvisionDefaultKey_me,
): mutation is ProvisionDefaultKey_me_UserMutation {
  return (
    (mutation as ProvisionDefaultKey_me_UserMutation).provisionKey !== undefined
  );
}

interface Props {
  graph: PublishSchemaGraphFragment;
  graphId: string;
  graphVariant: string;
  /**
   * User has selected a federated workflow
   */
  federated: boolean;
  /**
   * Via update modal
   */
  forUpdate?: boolean;
  /**
   * Schema was sent so we're completing the modal
   */
  onComplete?: () => void;
  /**
   * Cancel publishing the schema
   */
  onClose?: () => void;
  __test?: {
    initialTab?: PublishModeTab;
    staging?: boolean;
  };
}

export const PublishYourSchema = ({
  graph,
  graphId,
  graphVariant,
  federated,
  forUpdate = false,
  onClose,
  onComplete = onClose,
  __test,
}: Props) => {
  const { cache } = useApolloClient();
  const [accountId] = useCurrentAccountId();
  const [currentTab, setCurrentTab] = useState<PublishModeTab>(
    __test?.initialTab ?? PublishModeTab.SchemaReporting,
  );
  const { data } = useQuery(publishSchemaApiKeysQuery, {
    variables: { graphId },
  });
  const [
    finalizeProvisionKey,
    {
      data: provisionKeyData,
      error: provisionKeyError,
      loading: isProvisionKeyLoading,
      called: isProvisionKeyCalled,
    },
  ] = useMutation(provisionDefaultKey, {});

  useEffect(() => {
    finalizeProvisionKey();
  }, [finalizeProvisionKey]);

  const handleComplete = useCallback(() => {
    // Update the cache for the configured, classic graphs before triggering 'onComplete'
    if (graph.graphType === GraphType.CLASSIC) {
      cache.evict({
        id: cache.identify({
          __typename: 'Account',
          id: accountId,
          readField: {
            fieldName: 'graphsConnection',
            args: {
              filterBy: {
                state: 'CONFIGURED',
                type: ['CLASSIC', 'SELF_HOSTED_SUPERGRAPH'],
              },
            },
          },
        }),
      });
    }
    onComplete?.();
  }, [accountId, cache, graph.graphType, onComplete]);

  // if !updating and variant becomes published, fire the onComplete handler.
  // TODO(tim): maybe it would be better to handle this by seeing if there's
  // a newer publish than our last one, then we can cover the "update" case
  // as well as the initial publish.  right now, the "update" case isn't able
  // to tell if we should fire onComplete and it never will.
  const firstPublish = !forUpdate;
  useEffect(() => {
    const nowPublished = isPublished(graph);
    // also this will only happen for the old monolith flow - the new
    // federated flow will allow the user to click on the launches link
    // instead of automatically redirecting.
    if (!federated && firstPublish && nowPublished) {
      handleComplete();
    }
  }, [federated, graph, firstPublish, cache, accountId, handleComplete]);

  const serviceApiToken =
    data?.graph?.apiKeys?.[0]?.token || '[Token Unavailable]';

  if (!isProvisionKeyCalled || isProvisionKeyLoading) {
    return (
      <div className="flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (provisionKeyError) {
    throw new Error(provisionKeyError.message);
  }

  if (!provisionKeyData?.me || !isUserMutation(provisionKeyData.me)) {
    throw new Error('Cannot provision key for non-User identity');
  }

  if (!provisionKeyData.me.provisionKey) {
    throw new Error(
      `Failed to provision key for user ${provisionKeyData.me.user.id}`,
    );
  }

  const userToken = provisionKeyData.me.provisionKey.apiKey.token;
  if (!userToken) {
    return (
      <ErrorMessage className="mb-10">
        In order to publish a schema, you will need to use a Studio API Key. It
        appears you do not currently have any API keys enabled; visit the{' '}
        <Link to={userSettingsRouteConfig.location({})}>
          personal settings page
        </Link>{' '}
        to create one now.
      </ErrorMessage>
    );
  }
  if (federated) {
    return (
      <PublishUsingFederation
        firstPublish={firstPublish}
        graphId={graphId}
        graphVariant={graphVariant}
        serviceApiToken={serviceApiToken || '[Token Unavailable]'}
        isPublished={isPublished(graph)}
        hasBuildErrors={hasBuildErrors(graph)}
        onClose={onClose}
      />
    );
  }

  return (
    <>
      <div
        data-testid="register-schema-instructions"
        className="-mx-10 -mt-2 border-b border-primary px-8"
      >
        <HeaderTab
          tabName="Apollo Server"
          tabID={PublishModeTab.SchemaReporting}
          isActive={() => currentTab === PublishModeTab.SchemaReporting}
          onClick={() => setCurrentTab(PublishModeTab.SchemaReporting)}
        />
        <HeaderTab
          tabName="CLI"
          tabID={PublishModeTab.CI}
          isActive={() => currentTab === PublishModeTab.CI}
          onClick={() => setCurrentTab(PublishModeTab.CI)}
        />
        <HeaderTab
          tabName="Local introspection"
          tabID={PublishModeTab.Endpoint}
          isActive={() => currentTab === PublishModeTab.Endpoint}
          onClick={() => setCurrentTab(PublishModeTab.Endpoint)}
        />
        <HeaderTab
          tabName="Upload schema"
          tabID={PublishModeTab.SchemaFile}
          isActive={() => currentTab === PublishModeTab.SchemaFile}
          onClick={() => setCurrentTab(PublishModeTab.SchemaFile)}
        />
      </div>
      <div className="mt-4">
        {currentTab === PublishModeTab.Endpoint && (
          <EndpointTab
            firstPublish={firstPublish}
            graphId={graphId}
            graphVariant={graphVariant}
            onComplete={handleComplete}
            setTab={(tab) => setCurrentTab(tab)}
          />
        )}
        {currentTab === PublishModeTab.SchemaFile && (
          <SchemaFileTab
            firstPublish={firstPublish}
            graphId={graphId}
            graphVariant={graphVariant}
            document={graph.variant?.latestPublication?.schema.document}
            onComplete={handleComplete}
            setTab={(tab) => setCurrentTab(tab)}
          />
        )}
        {currentTab === PublishModeTab.SchemaReporting && (
          <SchemaReportingTab
            firstPublish={firstPublish}
            graphId={graphId}
            graphVariant={graphVariant}
            serviceApiToken={serviceApiToken}
            __test={__test}
            onClose={onClose}
          />
        )}
        {currentTab === PublishModeTab.Federation && (
          <FederationTab
            firstPublish={firstPublish}
            graphId={graphId}
            graphVariant={graphVariant}
            serviceApiToken={serviceApiToken}
            hasBuildErrors={hasBuildErrors(graph)}
            onClose={onClose}
          />
        )}
        {currentTab === PublishModeTab.CI && (
          <ContinuousIntegrationTab
            firstPublish={firstPublish}
            graphId={graphId}
            graphVariant={graphVariant}
            serviceApiToken={serviceApiToken}
            onClose={onClose}
          />
        )}
      </div>
    </>
  );
};
